import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Plant } from "../types";
import ImageGenerator from "./ImageGenerator";
import "./ImageGenerator.css";

interface PlantEditFormProps {
  plant: Plant | null;
  _onSave: (plant: Plant) => void;
}

const PlantEditForm: React.FC<PlantEditFormProps> = ({ plant, _onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Plant>({
    plant_id: "",
    common_name: "",
    scientific_name: "",
    family: "",
    genus: "",
    image_url: "",
    tags: [],
  });

  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    if (plant) {
      setFormData({
        ...plant,
        image_url: plant.image_url || "",
        tags: plant.tags || [],
      });
      // Initialize tags from plant data or use default empty array
      setTags(plant.tags || []);
    }
  }, [plant]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedFormData);
    _onSave({ ...updatedFormData, tags });
  };

  const updateFormDataTags = (newTags: string[]) => {
    const updatedFormData = {
      ...formData,
      tags: newTags,
    };
    setFormData(updatedFormData);
    _onSave(updatedFormData);
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const updatedTags = [...tags, newTag.trim()];
      setTags(updatedTags);
      updateFormDataTags(updatedTags);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);
    updateFormDataTags(updatedTags);
  };

  const handleImageGenerated = (imageUrl: string) => {
    const updatedFormData = {
      ...formData,
      image_url: imageUrl,
    };
    setFormData(updatedFormData);
    _onSave({ ...updatedFormData, tags });
  };

  return (
    <div className="plant-edit-form">
      <div className="form-group">
        <label htmlFor="common_name">{t('forms.labels.commonName')}</label>
        <input
          type="text"
          className="form-control"
          id="common_name"
          name="common_name"
          value={formData.common_name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="scientific_name">{t('forms.labels.scientificName')}</label>
        <input
          type="text"
          className="form-control"
          id="scientific_name"
          name="scientific_name"
          value={formData.scientific_name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="family">{t('forms.labels.family')}</label>
        <input
          type="text"
          className="form-control"
          id="family"
          name="family"
          value={formData.family}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="genus">{t('forms.labels.genus')}</label>
        <input
          type="text"
          className="form-control"
          id="genus"
          name="genus"
          value={formData.genus}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label>{t('admin.formLabels.tags')}</label>
        <div className="tags-container">
          {tags.map((tag) => (
            <div key={tag} className="tag">
              {tag}
              <span className="remove-tag" onClick={() => removeTag(tag)}>
                ×
              </span>
            </div>
          ))}
        </div>
        <div className="add-tag-input">
          <input
            type="text"
            className="form-control"
            placeholder={t('admin.formLabels.addTagPlaceholder')}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addTag()}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={addTag}
          >
            {t('admin.formLabels.addTag')}
          </button>
        </div>
      </div>

      {/* Image Generator */}
      <ImageGenerator
        initialImageUrl={formData.image_url}
        itemName={formData.common_name || "plant"}
        itemType="plant"
        itemId={formData.plant_id}
        onImageGenerated={handleImageGenerated}
      />
    </div>
  );
};

export default PlantEditForm;
